import type { ClientPortal } from '#api/clientSettingsApi.ts';
import { hslContrastRatio, hslLuminance } from '#lib/utils/color.ts';

let fontLinkElement: HTMLLinkElement | null = null;

const LIGHT_TEXT_HSL = '0 0% 100%';
const DARK_TEXT_HSL = '222 47% 11%';

function readableTextHsl(backgroundHsl: string): string {
    const lum = hslLuminance(backgroundHsl);
    if (lum === null) return LIGHT_TEXT_HSL;
    return lum > 0.55 ? DARK_TEXT_HSL : LIGHT_TEXT_HSL;
}

function setCssProperties(
    el: HTMLElement,
    record: Record<string, string>,
    prefix: string,
    skipKeys?: Set<string>,
    suffix?: string
) {
    for (const key in record) {
        if (skipKeys?.has(key)) continue;
        if (Object.hasOwnProperty.call(record, key)) {
            el.style.setProperty(`${prefix}${key}${suffix ?? ''}`, record[key]);
        }
    }
}

// For each background-like color the client supplied, derive a readable
// foreground (-foreground) variable when one wasn't explicitly provided.
// Means a client theming background-alt to white automatically gets dark text.
function deriveForegrounds(el: HTMLElement, colors: Record<string, string>) {
    for (const key of Object.keys(colors)) {
        if (key.endsWith('-foreground')) continue;
        const fgKey = `${key}-foreground`;
        if (colors[fgKey]) continue;
        el.style.setProperty(`--${fgKey}`, readableTextHsl(colors[key]));
    }
}

function loadGoogleFont(document: Document, url: string) {
    if (!fontLinkElement) {
        fontLinkElement = document.createElement('link');
        fontLinkElement.rel = 'stylesheet';
        document.head.appendChild(fontLinkElement);
    }
    if (fontLinkElement.href !== url) {
        fontLinkElement.href = url;
    }
}

const applyTheme = (document: Document, settings: ClientPortal) => {
    const themeData = settings.themeData;
    if (!themeData) return;

    const { colors, fonts, variables } = themeData as {
        colors?: Record<string, string>;
        fonts?: Record<string, string>;
        variables?: Record<string, string>;
    };

    const root = document.documentElement;

    if (colors) {
        setCssProperties(root, colors, '--');
        deriveForegrounds(root, colors);
    }

    // Toggle outside the `if (colors)` guard so a subsequent theme without
    // colors clears any stale class from a previous apply. WCAG 3:1 is the
    // non-text UI threshold used to decide whether --primary can stand in
    // for the CTA surface against --background-alt.
    const primary = colors?.['primary'];
    const bgAlt = colors?.['background-alt'];
    const ratio = primary && bgAlt ? hslContrastRatio(primary, bgAlt) : null;
    root.classList.toggle('primary-on-alt-ok', (ratio ?? 0) >= 3);

    if (fonts) {
        if (fonts.googleFontsUrl) loadGoogleFont(document, fonts.googleFontsUrl);
        setCssProperties(root, fonts, '--font-', new Set(['googleFontsUrl']), '-override');
    }

    if (variables) setCssProperties(root, variables, '--');
};

export default (document: Document, settings: ClientPortal) => {
    applyTheme(document, settings);

    return {
        update(settings: ClientPortal) {
            applyTheme(document, settings);
        }
    };
};
